export type SeminarPromoCodeType = 'free' | 'fixed' | 'percent';

export interface SeminarPromoCode {
  id: string;
  /** Großbuchstaben-Code ohne Leerzeichen, z. B. "VIP", "GAST100", "SPAR150" */
  code: string;
  /** 'free' = 100 % kostenlos (0 €), 'fixed' = fester €-Abzug netto, 'percent' = %-Abzug */
  type: SeminarPromoCodeType;
  /** Bei 'fixed': Netto-Euro-Betrag (z. B. 150); bei 'percent': Prozentsatz (z. B. 20); bei 'free': 100 */
  value: number;
  /** true = Rabatt gilt auch auf Zusatzpersonen; false/undefined = nur auf Hauptperson (Basispreis) */
  applyToExtras?: boolean;
}

const roundMoney = (amount: number): number =>
  Math.round((Number(amount || 0) + Number.EPSILON) * 100) / 100;

export const normalizePromoCodeString = (raw: unknown): string =>
  String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '');

export function normalizePromoCodes(raw: unknown): SeminarPromoCode[] {
  if (!Array.isArray(raw)) return [];
  const result: SeminarPromoCode[] = [];

  raw.forEach((item: any, idx: number) => {
    if (!item || typeof item !== 'object') return;
    const code = normalizePromoCodeString(item.code);
    if (!code) return;
    const rawType = String(item.type || 'fixed');
    const type: SeminarPromoCodeType =
      rawType === 'free' || rawType === 'percent' || rawType === 'fixed'
        ? rawType
        : 'fixed';
    const rawVal = Number(item.value);
    const value =
      type === 'free'
        ? 100
        : Number.isFinite(rawVal) && rawVal >= 0
          ? rawVal
          : 0;

    result.push({
      id: String(item.id || `promo_${idx}_${code}`),
      code,
      type,
      value,
      applyToExtras: item.applyToExtras === true,
    });
  });

  return result;
}

export function createEmptyPromoCode(): SeminarPromoCode {
  return {
    id: `promo_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    code: '',
    type: 'free',
    value: 100,
    applyToExtras: false,
  };
}

export function findMatchingPromoCode(
  inputCode: string,
  promoCodes?: SeminarPromoCode[] | null
): SeminarPromoCode | null {
  const clean = normalizePromoCodeString(inputCode);
  if (!clean || !Array.isArray(promoCodes) || promoCodes.length === 0) {
    return null;
  }
  return (
    promoCodes.find((p) => normalizePromoCodeString(p.code) === clean) || null
  );
}

export function formatPromoCodeBadgeLabel(promo: SeminarPromoCode): string {
  if (promo.type === 'free') {
    return `${promo.code} (Kostenlose Einladung · 100 % Rabatt)`;
  }
  if (promo.type === 'percent') {
    return `${promo.code} (−${promo.value} % Rabatt)`;
  }
  return `${promo.code} (−${promo.value.toLocaleString('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} € Rabatt)`;
}

export interface SeminarPriceCalculationResult {
  basisPreis: number;
  zusatzPreisTotal: number;
  originalNetto: number;
  discountNetto: number;
  discountLabel: string;
  nettoGesamt: number;
  mwst: number;
  bruttoGesamt: number;
}

export function calculateSeminarPricingWithPromo(opts: {
  basisPreis: number;
  zusatzPreisTotal: number;
  vatRate?: number;
  promo?: SeminarPromoCode | null;
}): SeminarPriceCalculationResult {
  const basisPreis = roundMoney(Math.max(0, Number(opts.basisPreis) || 0));
  const zusatzPreisTotal = roundMoney(
    Math.max(0, Number(opts.zusatzPreisTotal) || 0)
  );
  const originalNetto = roundMoney(basisPreis + zusatzPreisTotal);
  const vatRate =
    opts.vatRate !== undefined && opts.vatRate !== null
      ? Number(opts.vatRate)
      : 0.19;

  const promo = opts.promo;
  if (!promo || !promo.code) {
    const mwst = roundMoney(originalNetto * vatRate);
    const bruttoGesamt = roundMoney(originalNetto + mwst);
    return {
      basisPreis,
      zusatzPreisTotal,
      originalNetto,
      discountNetto: 0,
      discountLabel: '',
      nettoGesamt: originalNetto,
      mwst,
      bruttoGesamt,
    };
  }

  const discountBase = promo.applyToExtras ? originalNetto : basisPreis;
  let rawDiscount = 0;

  if (promo.type === 'free') {
    rawDiscount = discountBase;
  } else if (promo.type === 'percent') {
    const pct = Math.min(100, Math.max(0, Number(promo.value) || 0));
    rawDiscount = roundMoney(discountBase * (pct / 100));
  } else {
    rawDiscount = Math.max(0, Number(promo.value) || 0);
  }

  const discountNetto = roundMoney(Math.min(discountBase, rawDiscount));
  const nettoGesamt = roundMoney(Math.max(0, originalNetto - discountNetto));
  const mwst = roundMoney(nettoGesamt * vatRate);
  const bruttoGesamt = roundMoney(nettoGesamt + mwst);

  return {
    basisPreis,
    zusatzPreisTotal,
    originalNetto,
    discountNetto,
    discountLabel: formatPromoCodeBadgeLabel(promo),
    nettoGesamt,
    mwst,
    bruttoGesamt,
  };
}
